import { forwardRef, Fragment, ReactElement, useEffect } from "react";
import { isString } from "lodash";
import { Listbox as HeadlessListbox, Transition } from "@headlessui/react";
import { useFloating, autoUpdate, flip, offset } from "@floating-ui/react-dom";
import { HiOutlineChevronUp } from "react-icons/hi";
import { FieldError } from "react-hook-form";
import cx from "classnames";
import { Typography } from "@earned/core";
import InputError from "../InputError";
import { themeTextColors } from "@common/constants";

interface SelectProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  options: any[];
  autoFocus?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  renderOption: (value: any) => string | ReactElement;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange?: (value: any) => void;
  className?: string;
  buttonClassName?: string;
  optionClassName?: string;
  error?: FieldError;
  required?: boolean;
}

const Select = forwardRef<HTMLElement, SelectProps>(
  ({
    value,
    options,
    renderOption,
    autoFocus,
    onChange = () => null,
    error,
    className,
    buttonClassName,
    optionClassName,
    required,
    ...rest
  }) => {
    const { x, y, reference, floating, strategy, update, refs } = useFloating({
      placement: "bottom-start",
      strategy: "absolute",
      middleware: [offset(2), flip()],
    });
    useEffect(() => {
      if (!refs.reference.current || !refs.floating.current) {
        return;
      }
      return autoUpdate(refs.reference.current, refs.floating.current, update);
    }, [refs.reference, refs.floating, update]);

    return (
      <HeadlessListbox value={value} onChange={onChange} {...rest}>
        {({ open }) => (
          <div className={cx("relative w-full bg-white", className || "")}>
            <HeadlessListbox.Button
              ref={reference}
              autoFocus={autoFocus}
              className={cx(
                ` flex h-10 w-full flex-row items-center justify-between rounded border border-gray-400 bg-white px-[11px] py-2 text-body font-normal text-gray-700 placeholder:text-gray-500 hover:border-gray-500 focus:border-green-3 focus:outline-none focus:ring-0 active:border-gray-600 disabled:bg-disabled disabled:text-disabled `,
                error ? "border-red-600 " : "",
                buttonClassName ?? ""
              )}
            >
              {value ? (
                <span className="truncate">{renderOption(value)}</span>
              ) : (
                <span className="text-green-500">{renderOption(value)}</span>
              )}
              <span className="pointer-events-none -mr-[5px] flex items-center pl-1">
                <HiOutlineChevronUp
                  size={24}
                  color={themeTextColors.primary}
                  aria-hidden="true"
                  className={cx("transition-all", open ? "" : "rotate-180")}
                />
              </span>
            </HeadlessListbox.Button>
            <div
              ref={floating}
              style={{
                position: strategy,
                top: y ?? "",
                left: x ?? "",
              }}
              className="absolute w-full"
            >
              <Transition
                as={Fragment}
                leave="transition ease-in duration-300"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <HeadlessListbox.Options className="absolute z-10 max-h-64 w-fit overflow-auto rounded-md bg-white py-1 text-label-base shadow-lg ring-1 ring-black ring-opacity-5 scrollbar-thin focus:outline-none">
                  {options.map((option) => (
                    <HeadlessListbox.Option
                      key={isString(option) ? option : option?.id}
                      value={option}
                      className={({ active }) =>
                        `relative flex h-11 cursor-pointer select-none items-center p-3 text-primary ${
                          active ? "bg-green-50" : "bg-white"
                        }`
                      }
                    >
                      {({ selected }) => (
                        <Typography
                          component="span"
                          weight={selected ? "medium" : "normal"}
                          variant="body"
                          className={optionClassName}
                        >
                          {renderOption(option)}
                        </Typography>
                      )}
                    </HeadlessListbox.Option>
                  ))}
                </HeadlessListbox.Options>
              </Transition>
            </div>
            <InputError value={error?.message} />
          </div>
        )}
      </HeadlessListbox>
    );
  }
);

Select.displayName = "Select";

export default Select;
